/**
 * DAFNE Insulin Configuration
 *
 * Insulin activity parameters, IOB calculations, and correction formulas.
 * Based on OZ DAFNE guidelines for rapid-acting insulin.
 *
 * Last reviewed: February 2026
 */

// =============================================================================
// INSULIN ON BOARD (IOB) PARAMETERS
// =============================================================================

export const IOB_CONFIG = {
  /** Duration of quick-acting insulin activity (hours) */
  QA_DURATION_HOURS: 4,

  /** Duration window for querying recent entries for IOB (hours) */
  IOB_QUERY_WINDOW_HOURS: 4,

  /** Duration window for loading entries context (hours) */
  ENTRY_LOAD_WINDOW_HOURS: 24,
} as const;

// =============================================================================
// DEFAULT CORRECTION FACTOR
// =============================================================================

export const CORRECTION_CONFIG = {
  /** Default ISF: null means user must set from their DAFNE course */
  DEFAULT_CORRECTION_FACTOR: null as number | null,

  /** Minimum correction factor (most sensitive) */
  MIN_CORRECTION_FACTOR: 0.5,

  /** Maximum correction factor (least sensitive) */
  MAX_CORRECTION_FACTOR: 10.0,
};

// =============================================================================
// OZDAFNE EFFECT RANGES
// How much 1 unit of insulin or 1 CP affects blood glucose
// =============================================================================

export const EFFECT_RANGES = {
  /**
   * OZDAFNE: 1 unit of quick-acting insulin changes BG by 2.0-3.0 mmol/L
   * - 1 extra unit lowers BG by 2.0-3.0 mmol/L
   * - 1 unit less raises BG by 2.0-3.0 mmol/L
   */
  INSULIN: {
    MIN: 2.0,
    MAX: 3.0,
    TYPICAL: 2.5,
    DESCRIPTION: "1 unit QA changes BG by 2.0-3.0 mmol/L",
  },

  /**
   * OZDAFNE: 1 CP (10g carbs) raises BG by 2.0-3.0 mmol/L
   */
  CARBOHYDRATE: {
    MIN: 2.0,
    MAX: 3.0,
    TYPICAL: 2.5,
    DESCRIPTION: "1 CP (10g carbs) raises BG by 2.0-3.0 mmol/L",
  },
} as const;

// =============================================================================
// EXERCISE ADJUSTMENT PERCENTAGES
// =============================================================================

export const EXERCISE_ADJUSTMENTS = {
  /** Pre-exercise QA reduction percentages by intensity */
  PRE_EXERCISE: {
    LIGHT: 15, // Base percentage per hour
    MODERATE: 30,
    VIGOROUS: 50,
  },

  /** Post-exercise QA reduction percentages by intensity */
  POST_EXERCISE: {
    LIGHT: 10,
    MODERATE: 20,
    VIGOROUS: 30,
  },

  /** Maximum QA reduction caps */
  CAPS: {
    PRE_EXERCISE_MAX: 50,
    POST_EXERCISE_MAX: 30,
  },

  /** Extra carbs alternatives (grams per hour of exercise) */
  EXTRA_CARBS: {
    PRE_LIGHT: 10,
    PRE_MODERATE: 15,
    PRE_VIGOROUS: 25,
    POST_LIGHT: 5,
    POST_MODERATE: 10,
    POST_VIGOROUS: 15,
    PRE_MAX: 40,
    POST_MAX: 20,
  },
} as const;

// =============================================================================
// DEFAULT CARB:INSULIN RATIOS
// STORAGE FORMAT: units per CP (e.g., 2.5 = 2.5 units per 10g carbs)
// User MUST set these - null means not configured
// =============================================================================

export const DEFAULT_CP_RATIOS = {
  /** Breakfast ratio - must be set by user from their DAFNE course */
  B: null as number | null,

  /** Lunch ratio */
  L: null as number | null,

  /** Evening meal ratio */
  EM: null as number | null,

  /** Bedtime ratio */
  BT: null as number | null,

  /** 3AM check ratio */
  "3AM": null as number | null,
};

// =============================================================================
// CP RATIO UTILITIES - SINGLE SOURCE OF TRUTH
// Storage: units per CP (10g)
// =============================================================================

/**
 * Backward-compatible alias retained to avoid widespread call-site churn.
 * Stored values are now already units-per-CP, so this is identity.
 */
export function gramsPerUnitToUnitsPerCP(
  unitsPerCP: number | null | undefined,
): number {
  if (!unitsPerCP || unitsPerCP <= 0) return 0;
  return unitsPerCP;
}

/**
 * Backward-compatible alias retained to avoid widespread call-site churn.
 * Stored values are now already units-per-CP, so this is identity.
 */
export function unitsPerCPToGramsPerUnit(unitsPerCP: number): number {
  if (unitsPerCP <= 0) return 0;
  return unitsPerCP;
}

/**
 * Get units per CP for a period from settings, with optional rounding.
 */
export function getUnitsPerCPFromSettings(
  cpRatios: Record<string, number | null> | undefined,
  period: string,
  roundToHalf: boolean = true,
): number {
  const unitsPerCP = cpRatios?.[period] ?? 0;
  return roundToHalf ? Math.round(unitsPerCP * 2) / 2 : unitsPerCP;
}

/**
 * Calculate quick-acting insulin dose for carbs.
 *
 * @param cpCount - Number of CP (10g portions)
 * @param cpRatios - Settings cpRatios object (stored as units per CP)
 * @param period - Period key (B, L, EM, etc.)
 * @param doseIncrement - Rounding increment (default 0.5)
 * @returns Suggested insulin units, or 0 if ratio not configured
 */
export function calculateQADoseForCarbs(
  cpCount: number,
  cpRatios: Record<string, number | null> | undefined,
  period: string,
  doseIncrement: number = 0.5,
): number {
  const unitsPerCP = cpRatios?.[period] ?? 0;
  if (!unitsPerCP) return 0;

  const rawDose = cpCount * unitsPerCP;
  return Math.round(rawDose / doseIncrement) * doseIncrement;
}

/**
 * Format ratio for display as "Xu/CP" or "X units/CP"
 */
export function formatRatioDisplay(
  unitsPerCP: number | null | undefined,
  short: boolean = true,
): string {
  if (!unitsPerCP) return short ? "—" : "Not set";
  const rounded = Math.round(unitsPerCP * 2) / 2;
  const display = Number.isInteger(rounded)
    ? rounded.toString()
    : rounded.toFixed(1);
  return short ? `${display}u/CP` : `${display} units/CP`;
}

// =============================================================================
// EXTRA PROTEIN/FAT QUICK-ACTING INSULIN (P/F)
// Source: OZDAFNE Protein and Fat Dosing handout, Version 4, August 2025
// =============================================================================

/**
 * Configuration for the OZDAFNE "Extra Protein/Fat quick-acting insulin" (P/F)
 * dose, taken alongside the mealtime quick dose for meals that are high in
 * protein and/or fat.
 *
 * Source: OZDAFNE Protein and Fat Dosing handout, Version 4, August 2025
 * (NDSS / Diabetes Australia / Diabetes Victoria; advised by Prof Bruce King,
 * Dr Carmel Smart, Emma Fisher, Dr Brigid Knight).
 */
export const PROTEIN_FAT_CONFIG = {
  /** Starting P/F dose: 20% of the QA dose for CPs (with-carbs path). */
  STARTING_PERCENT: 20,
  /** Escalated P/F dose after two failed occasions at the starting percent. */
  ESCALATED_PERCENT: 30,
  /** Hours after the meal at which the user verifies the P/F dose worked. */
  PEAK_CHECK_HOURS: [3, 5] as const,
  /** No-carb meals: count the protein/fat as 1 CP (the "0 + 1" convention). */
  NO_CARB_CP_EQUIVALENT: 1,
  /** Citation source for any user-facing reference. */
  SOURCE: "OZDAFNE Protein and Fat Dosing handout, Version 4, August 2025",
} as const;

/** Allowed P/F starting / escalated percent values. */
export type PFPercent =
  | typeof PROTEIN_FAT_CONFIG.STARTING_PERCENT
  | typeof PROTEIN_FAT_CONFIG.ESCALATED_PERCENT;

/**
 * Calculate the Extra P/F quick-acting insulin dose for a high protein/fat
 * meal that DOES contain carbohydrate.
 *
 * OZDAFNE (Version 4, Aug 2025): "Work out 20% of the quick-acting insulin
 * dose you need to take for your CPs ... [round up]." Escalates to 30% after
 * two failed occasions at 20%.
 *
 * Note: rounding is **ceil** (handout uses "round up" — 0.8u → 1u, 1.6u → 2u),
 * which differs from `calculateQADoseForCarbs` (round to nearest).
 *
 * @param qaBaseForCarbs - The QA dose for the meal's carbs (units), step 3
 *                        of the OZDAFNE process.
 * @param percent - 20 (starting) or 30 (escalated).
 * @param doseIncrement - Minimum dose step (e.g. 0.5 for half-unit pens).
 * @returns The P/F dose in units, rounded up to `doseIncrement`.
 */
export function calculatePFDoseWithCarbs(
  qaBaseForCarbs: number,
  percent: PFPercent,
  doseIncrement: number = 0.5,
): number {
  if (qaBaseForCarbs <= 0 || doseIncrement <= 0) return 0;
  const raw = qaBaseForCarbs * (percent / 100);
  // Tolerance protects against IEEE-754 drift (e.g. 0.8 / 0.1 = 8.0000000000002,
  // which would otherwise ceil to 9 and over-dose a pump user by 0.1u).
  const steps = Math.ceil(raw / doseIncrement - 1e-9);
  return Math.round(steps * doseIncrement * 1_000_000) / 1_000_000;
}

/**
 * Calculate the Extra P/F quick-acting insulin dose for a high protein/fat
 * meal that has NO carbohydrate.
 *
 * OZDAFNE (Version 4, Aug 2025): "count the protein and/or fat as 1 CP ...
 * multiply your mealtime quick-acting insulin ratio by 1 CP". So the dose is
 * simply the user's per-period ratio, rounded to `doseIncrement`.
 *
 * @param cpRatios - User settings cpRatios (units per CP), keyed by period.
 * @param period - Period key (B, L, EM, BT, 3AM).
 * @param doseIncrement - Minimum dose step (e.g. 0.5 for half-unit pens).
 * @returns The P/F dose in units, or 0 if the period ratio is unset.
 */
export function calculatePFDoseNoCarbs(
  cpRatios: Record<string, number | null> | undefined,
  period: string,
  doseIncrement: number = 0.5,
): number {
  return calculateQADoseForCarbs(
    PROTEIN_FAT_CONFIG.NO_CARB_CP_EQUIVALENT,
    cpRatios,
    period,
    doseIncrement,
  );
}

// =============================================================================
// IOB CALCULATION FUNCTION
// =============================================================================

/**
 * Calculate remaining active insulin from a dose using linear decay model.
 *
 * DAFNE Formula: IOB = dose × (1 - hoursElapsed / duration)
 *
 * @param doseUnits - Original insulin dose in units
 * @param hoursElapsed - Hours since the dose was given
 * @param durationHours - Total activity duration (default: QA_DURATION_HOURS)
 * @returns Remaining active insulin in units
 */
export function calculateIOB(
  doseUnits: number,
  hoursElapsed: number,
  durationHours: number = IOB_CONFIG.QA_DURATION_HOURS,
): number {
  if (hoursElapsed >= durationHours || hoursElapsed < 0) return 0;

  // Linear decay model (simple DAFNE approximation)
  // More accurate models use exponential or bilinear curves
  const remaining = doseUnits * (1 - hoursElapsed / durationHours);
  return Math.max(0, remaining);
}

/**
 * Calculate correction dose using DAFNE formula.
 *
 * DAFNE Formula: correction = (currentBG - targetBG) / ISF - IOB
 *
 * @param currentBG - Current blood glucose (mmol/L)
 * @param targetBG - Target blood glucose (mmol/L)
 * @param correctionFactor - Insulin sensitivity factor (ISF)
 * @param activeIOB - Current insulin on board
 * @returns Suggested correction units (can be negative)
 */
export function calculateCorrectionDose(
  currentBG: number,
  targetBG: number,
  correctionFactor: number,
  activeIOB: number,
): number {
  const rawCorrection = (currentBG - targetBG) / correctionFactor;
  return rawCorrection - activeIOB;
}
